import React, { useEffect, useRef, useState, useCallback } from "react";
import { useParams, Link, useNavigate, useSearchParams } from "react-router-dom";
import { PlanStatusProvider, AddToPlanBtn } from "@/components/dailyplan/AddToPlan";
import { api, extractError } from "@/lib/api";
import { toast } from "sonner";
import { formatDateIt, taskTypeMeta, TASK_TYPES, productivityRatio, productivityClass } from "@/lib/format";
import { formatHM, parseHM } from "@/lib/hours";
import { ArrowLeft, Copy, CheckCircle2, Circle, Trash2, Plus, Pencil, X, ListTree, Lock, Send, BookMarked, MoreVertical, Download } from "lucide-react";
import MentionPopover, { useMention } from "@/components/MentionPopover";
import { sanitizeHtml } from "@/lib/sanitize";

const TABS = [
    { key: "strategia", label: "Strategia" },
    { key: "programmi", label: "Programmi" },
    { key: "secondary", label: "Task" },
    { key: "tertiary", label: "Task secondarie" },
];

export default function ProjectDetail() {
    const { id } = useParams();
    const nav = useNavigate();
    const [searchParams] = useSearchParams();
    const backHref = searchParams.get("from") === "internal" ? "/progetti-interni" : "/progetti";
    const [project, setProject] = useState(null);
    const [client, setClient] = useState(null);
    const [clients, setClients] = useState([]);
    const [statuses, setStatuses] = useState([]);
    const [programs, setPrograms] = useState([]);
    const [tasks, setTasks] = useState([]);
    const [metrics, setMetrics] = useState({ project_percentage: 0, programs: {}, secondary_tasks: {} });
    const [tab, setTab] = useState(searchParams.get("tab") || "strategia");
    const focusTask = searchParams.get("task");
    const [showPct, setShowPct] = useState(true);
    const [showEditProject, setShowEditProject] = useState(false);
    const [feedbackFor, setFeedbackFor] = useState(null); // project object when just transitioned to Concluso
    const [showApplyProjTpl, setShowApplyProjTpl] = useState(false);
    const [showPdfExport, setShowPdfExport] = useState(false);


    const load = useCallback(async () => {
        const p = await api.get(`/projects/${id}`);
        setProject(p.data);
        const clientPromise = p.data.client_id
            ? api.get(`/clients/${p.data.client_id}`).catch(() => ({ data: null }))
            : Promise.resolve({ data: null });
        const [c, cs, s, prg, tk, m, prefs] = await Promise.all([
            clientPromise,
            api.get(`/clients`),
            api.get("/settings/project-statuses"),
            api.get(`/projects/${id}/programs`),
            api.get(`/projects/${id}/tasks`),
            api.get(`/projects/${id}/metrics`),
            api.get("/settings/preferences"),
        ]);
        setClient(c.data); setClients(cs.data); setStatuses(s.data);
        setPrograms(prg.data); setTasks(tk.data); setMetrics(m.data);
        setShowPct(prefs.data.show_completion_percentage !== false);
    }, [id]);

    useEffect(() => { load(); }, [load]);
    useEffect(() => {
        if (!focusTask || tasks.length === 0) return;
        const t = setTimeout(() => {
            const el = document.querySelector(`[data-testid="task-${focusTask}"]`);
            if (!el) return;
            el.scrollIntoView({ behavior: "smooth", block: "center" });
            el.classList.add("ring-2", "ring-[#FFE500]");
            setTimeout(() => el.classList.remove("ring-2", "ring-[#FFE500]"), 2500);
        }, 300);
        return () => clearTimeout(t);
    }, [focusTask, tasks.length]);

    if (!project) return <div className="p-6 text-sm text-neutral-500">Caricamento…</div>;
    const status = statuses.find((s) => s.id === project.status_id);

    const duplicate = async () => {
        if (!window.confirm("Duplicare il progetto? Verranno copiati piano, programmi e task.")) return;
        const { data } = await api.post(`/projects/${id}/duplicate`);
        nav(`/progetti/${data.id}`);
    };

    const deleteProject = async () => {
        if (!window.confirm(`Eliminare il progetto "${project.title}"? Verranno rimossi anche programmi e task collegati.`)) return;
        await api.delete(`/projects/${id}`);
        nav(backHref);
    };

    return (
        <div data-testid="project-detail-page">
            <div className="sticky top-0 z-10 bg-white border-b border-neutral-200">
                <div className="px-6 pt-4 pb-2 flex items-center gap-3">
                    <Link to={backHref} className="text-neutral-500 hover:text-neutral-800"><ArrowLeft size={16} /></Link>
                    <div className="min-w-0 flex-1">
                        <div className="text-xs text-neutral-500">
                            {{ continuous: "Progetto continuativo", internal_long: "Progetto interno a lungo termine", internal_weekly: "Progetto interno settimanale" }[project.project_type] || "Progetto spot"} · {client
                                ? <Link className="hover:underline" to={`/clienti/${client.id}`}>{client.first_name} {client.last_name}</Link>
                                : <span className="italic text-neutral-400" data-testid="no-client-label">Nessun cliente associato</span>
                            }
                        </div>
                        <h1 className="font-display text-xl font-semibold tracking-tight text-neutral-900 truncate" data-testid="project-title">{project.title}</h1>
                    </div>
                    {showPct && (
                        <div className="text-xs text-neutral-500 flex items-center gap-2">
                            <div className="w-32 h-2 bg-neutral-100 rounded overflow-hidden">
                                <div className="h-full bg-[#FFE500]" style={{ width: `${metrics.project_percentage}%` }} />
                            </div>
                            <span className="font-medium text-neutral-800">{metrics.project_percentage}%</span>
                        </div>
                    )}
                    <div className="text-xs text-neutral-500 flex items-center gap-1 whitespace-nowrap" data-testid="project-header-deadline">
                        Scadenza: <b className="text-neutral-800">{formatDateIt(project.end_date)}</b>
                    </div>
                    {status && <span className="text-xs px-2 py-0.5 rounded border" style={{ borderColor: status.color, color: status.color, background: status.color + "10" }}>{status.name}</span>}
                    <button onClick={() => setShowPdfExport(true)} data-testid="export-plan-pdf-btn"
                        className="text-xs border border-neutral-200 rounded px-2 py-1 hover:bg-neutral-50 inline-flex items-center gap-1 text-neutral-700"
                        title="Esporta piano in PDF">
                        <Download size={12} /> Esporta PDF
                    </button>
                    <ProjectActionsMenu
                        onApplyTemplate={() => setShowApplyProjTpl(true)}
                        onEdit={() => setShowEditProject(true)}
                        onDuplicate={duplicate}
                        onDelete={deleteProject}
                    />
                </div>
                <div className="px-6 flex gap-0 -mb-px" data-testid="project-tabs">
                    {TABS.map((t) => (
                        <button key={t.key} data-testid={`tab-${t.key}`} onClick={() => setTab(t.key)}
                            className={`px-4 py-2 text-sm border border-b-0 rounded-t-md -mr-px ${tab === t.key
                                ? "bg-white text-neutral-900 font-semibold border-neutral-200 border-t-2 border-t-[#FFE500]"
                                : "bg-neutral-50 text-neutral-500 border-neutral-200 hover:text-neutral-700"}`}>{t.label}
                        </button>
                    ))}
                </div>
            </div>

            <div className="p-6">
                {tab === "strategia" && <StrategyTab project={project} programs={programs} onChange={load} />}
                {tab === "programmi" && <ProgramsTab programs={programs} tasks={tasks} metrics={metrics} showPct={showPct} onChange={load} />}
                {tab === "secondary" && <PlanStatusProvider projectId={project.id} tasks={tasks}><TasksTab level="secondary" project={project} programs={programs} tasks={tasks} metrics={metrics} showPct={showPct} onChange={load} /></PlanStatusProvider>}
                {tab === "tertiary" && <PlanStatusProvider projectId={project.id} tasks={tasks}><TasksTab level="tertiary" project={project} programs={programs} tasks={tasks} metrics={metrics} showPct={showPct} onChange={load} /></PlanStatusProvider>}
            </div>

            {showEditProject && (
                <EditProjectModal project={project} clients={clients} statuses={statuses}
                    onClose={() => setShowEditProject(false)}
                    onSaved={(next) => {
                        setShowEditProject(false);
                        // If the status transitioned TO "Concluso", offer to send feedback email.
                        const newStatus = statuses.find((s) => s.id === next?.status_id);
                        const oldStatus = statuses.find((s) => s.id === project.status_id);
                        if (newStatus && (newStatus.name || "").toLowerCase() === "concluso"
                            && (oldStatus?.name || "").toLowerCase() !== "concluso") {
                            setFeedbackFor(next);
                        }
                        load();
                    }} />
            )}

            {feedbackFor && (
                <FeedbackModal projectId={feedbackFor.id} onClose={() => setFeedbackFor(null)} />
            )}

            {showApplyProjTpl && (
                <ApplyProjectTemplateModal projectId={project.id}
                    onClose={() => setShowApplyProjTpl(false)}
                    onApplied={() => { setShowApplyProjTpl(false); load(); }} />
            )}
            {showPdfExport && (
                <ExportPlanPdfModal project={project} onClose={() => setShowPdfExport(false)} />
            )}
        </div>
    );
}

function ExportPlanPdfModal({ project, onClose }) {
    const [strategy, setStrategy] = useState(true);
    const [programs, setPrograms] = useState(true);
    const [tasks, setTasks] = useState(true);
    const [secondaries, setSecondaries] = useState(true);
    const [busy, setBusy] = useState(false);
    const download = async () => {
        setBusy(true);
        try {
            const res = await api.get(`/projects/${project.id}/plan-pdf`, {
                params: {
                    strategy: strategy ? 1 : 0,
                    programs: programs ? 1 : 0,
                    tasks: tasks ? 1 : 0,
                    secondaries: secondaries ? 1 : 0,
                },
                responseType: "blob",
            });
            const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
            const a = document.createElement("a");
            a.href = url;
            a.download = `${(project.title || "progetto").replace(/[^a-zA-Z0-9._-]+/g, "_")}.pdf`;
            document.body.appendChild(a); a.click(); a.remove();
            URL.revokeObjectURL(url);
            onClose();
        } catch (e) { window.alert(extractError(e)); }
        finally { setBusy(false); }
    };
    return (
        <div className="fixed inset-0 bg-neutral-900/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
            <div onClick={(e) => e.stopPropagation()}
                data-testid="pdf-export-modal"
                className="bg-white rounded-md shadow-lg w-full max-w-sm">
                <div className="px-5 py-3 border-b border-neutral-200 font-display font-semibold">Esporta piano in PDF</div>
                <div className="p-5 space-y-2 text-sm">
                    <label className="flex items-center gap-2"><input type="checkbox" checked={strategy} onChange={(e) => setStrategy(e.target.checked)} data-testid="pdf-inc-strategy" /> Strategia</label>
                    <label className="flex items-center gap-2"><input type="checkbox" checked={programs} onChange={(e) => setPrograms(e.target.checked)} data-testid="pdf-inc-programs" /> Programmi</label>
                    <label className="flex items-center gap-2 pl-4"><input type="checkbox" checked={tasks} onChange={(e) => setTasks(e.target.checked)} disabled={!programs} data-testid="pdf-inc-tasks" /> Task</label>
                    <label className="flex items-center gap-2 pl-8"><input type="checkbox" checked={secondaries} onChange={(e) => setSecondaries(e.target.checked)} disabled={!programs || !tasks} data-testid="pdf-inc-secondaries" /> Task secondarie</label>
                </div>
                <div className="px-5 py-3 border-t border-neutral-100 flex justify-end gap-2 bg-neutral-50">
                    <button onClick={onClose} className="px-3 py-1.5 text-sm rounded border border-neutral-300 bg-white">Annulla</button>
                    <button onClick={download} disabled={busy} data-testid="pdf-download-btn"
                        className="px-3 py-1.5 text-sm rounded bg-neutral-900 text-white hover:bg-neutral-800 disabled:opacity-60">
                        {busy ? "Genero…" : "Scarica"}
                    </button>
                </div>
            </div>
        </div>
    );
}

function ProjectActionsMenu({ onApplyTemplate, onEdit, onDuplicate, onDelete }) {
    const [open, setOpen] = useState(false);
    const rootRef = useRef(null);
    useEffect(() => {
        const h = (e) => { if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false); };
        document.addEventListener("mousedown", h);
        return () => document.removeEventListener("mousedown", h);
    }, []);
    const item = (label, icon, onClick, danger) => (
        <button
            onClick={() => { setOpen(false); onClick(); }}
            data-testid={`project-menu-${label.toLowerCase().replace(/\s+/g, "-")}`}
            className={`w-full flex items-center gap-2 px-3 py-2 text-sm text-left ${danger ? "text-red-600 hover:bg-red-50" : "text-neutral-700 hover:bg-neutral-50"}`}
        >
            {icon} {label}
        </button>
    );
    return (
        <div ref={rootRef} className="relative">
            <button onClick={() => setOpen(!open)} data-testid="project-actions-btn"
                className="text-xs border border-neutral-300 bg-white text-neutral-700 hover:bg-neutral-50 rounded p-1.5 inline-flex items-center">
                <MoreVertical size={14} />
            </button>
            {open && (
                <div className="absolute right-0 top-full mt-1 bg-white border border-neutral-200 rounded-md shadow-lg w-48 z-30 py-1" data-testid="project-actions-menu">
                    {item("Applica template", <BookMarked size={13} />, onApplyTemplate)}
                    {item("Modifica", <Pencil size={13} />, onEdit)}
                    {item("Duplica", <Copy size={13} />, onDuplicate)}
                    <div className="border-t border-neutral-100 my-1" />
                    {item("Elimina", <Trash2 size={13} />, onDelete, true)}
                </div>
            )}
        </div>
    );
}

function ApplyProjectTemplateModal({ projectId, onClose, onApplied }) {
    const [templates, setTemplates] = useState([]);
    const [tid, setTid] = useState("");
    const [replace, setReplace] = useState(false);
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");
    useEffect(() => {
        api.get("/sop-templates", { params: { type: "project" } }).then((r) => setTemplates(r.data)).catch(() => {});
    }, []);
    const apply = async () => {
        if (!tid) return;
        if (replace && !window.confirm("Sostituire integralmente il progetto con il contenuto del template? Piano, programmi e task esistenti verranno cancellati.")) return;
        setBusy(true); setErr("");
        try { await api.post(`/sop-templates/${tid}/apply/project/${projectId}`, null, { params: { replace } }); onApplied(); }
        catch (e) { setErr(extractError(e)); } finally { setBusy(false); }
    };
    return (
        <div className="fixed inset-0 bg-neutral-900/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
            <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-md shadow-lg w-full max-w-md" data-testid="apply-project-template-modal">
                <div className="px-5 py-3 border-b border-neutral-200 flex items-center justify-between">
                    <div className="font-display font-semibold">Applica template al progetto</div>
                    <button onClick={onClose}><X size={16} /></button>
                </div>
                <div className="p-5 space-y-3">
                    {templates.length === 0 ? (
                        <div className="text-sm text-neutral-500 bg-neutral-50 border border-neutral-200 rounded px-3 py-2">
                            Nessun template di progetto disponibile. Creane uno in <Link to="/sop" className="underline">SOP</Link>.
                        </div>
                    ) : (
                        <label className="block">
                            <span className="text-xs text-neutral-600">Template</span>
                            <select value={tid} onChange={(e) => setTid(e.target.value)}
                                data-testid="select-project-template"
                                className="mt-1 w-full text-sm border border-neutral-300 rounded px-2 py-1.5">
                                <option value="">— Seleziona —</option>
                                {templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                            </select>
                        </label>
                    )}
                    <label className="flex items-start gap-2 text-sm">
                        <input type="checkbox" checked={replace} onChange={(e) => setReplace(e.target.checked)} data-testid="apply-project-replace-toggle" className="mt-0.5" />
                        <span>Sostituisci integralmente il contenuto del progetto (piano, programmi, task)</span>
                    </label>
                    {err && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">{err}</div>}
                </div>
                <div className="px-5 py-3 border-t border-neutral-100 flex justify-end gap-2 bg-neutral-50">
                    <button onClick={onClose} className="px-3 py-1.5 text-sm rounded border border-neutral-300 bg-white">Annulla</button>
                    <button onClick={apply} disabled={!tid || busy} data-testid="confirm-apply-project-template"
                        className="px-3 py-1.5 text-sm rounded bg-neutral-900 text-white hover:bg-neutral-800 disabled:opacity-60">
                        {busy ? "Applicazione…" : "Applica"}
                    </button>
                </div>
            </div>
        </div>
    );
}

// ---------------- Utility: children map ----------------
function buildChildCount(tertiaries) {
    const map = {};
    tertiaries.forEach((t) => {
        if (t.parent_task_id) map[t.parent_task_id] = (map[t.parent_task_id] || 0) + 1;
    });
    return map;
}

// ---------------- Edit project modal ----------------
function EditProjectModal({ project, clients, statuses, onClose, onSaved }) {
    const [form, setForm] = useState({
        title: project.title || "",
        project_type: project.project_type,
        client_id: project.client_id,
        status_id: project.status_id || "",
        end_date: project.end_date || "",
    });
    const [err, setErr] = useState("");
    const [busy, setBusy] = useState(false);
    const submit = async (e) => {
        e.preventDefault(); setBusy(true); setErr("");
        try { const { data } = await api.patch(`/projects/${project.id}`, form); onSaved(data); }
        catch (e) { setErr(extractError(e)); } finally { setBusy(false); }
    };
    return (
        <div className="fixed inset-0 bg-neutral-900/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
            <form onClick={(e) => e.stopPropagation()} onSubmit={submit} data-testid="edit-project-modal" className="bg-white rounded-md shadow-lg w-full max-w-lg">
                <div className="flex items-center justify-between px-5 py-3 border-b border-neutral-200">
                    <div className="font-display font-semibold">Modifica progetto</div>
                    <button type="button" onClick={onClose}><X size={16} /></button>
                </div>
                <div className="p-5 space-y-3">
                    <F label="Titolo*"><input required data-testid="edit-project-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="w-full px-2 py-1.5 border border-neutral-300 rounded text-sm" /></F>
                    <div className="grid grid-cols-2 gap-3">
                        <F label="Tipo">
                            <select data-testid="edit-project-type" value={form.project_type}
                                onChange={(e) => {
                                    const nt = e.target.value;
                                    const isInternal = nt === "internal_long" || nt === "internal_weekly";
                                    setForm({ ...form, project_type: nt, client_id: isInternal ? null : form.client_id });
                                }}
                                className="w-full px-2 py-1.5 border border-neutral-300 rounded text-sm">
                                <option value="spot">Spot</option>
                                <option value="continuous">Continuativo</option>
                                <option value="internal_long">Interno · Lungo termine</option>
                                <option value="internal_weekly">Interno · Settimanale</option>
                            </select>
                        </F>
                        {form.project_type !== "internal_long" && form.project_type !== "internal_weekly" && (
                            <F label="Cliente">
                                <select data-testid="edit-project-client" value={form.client_id || ""} onChange={(e) => setForm({ ...form, client_id: e.target.value })} className="w-full px-2 py-1.5 border border-neutral-300 rounded text-sm">
                                    <option value="">— Seleziona —</option>
                                    {clients.map((c) => <option key={c.id} value={c.id}>{c.first_name} {c.last_name}</option>)}
                                </select>
                            </F>
                        )}
                        <F label="Stato">
                            <select data-testid="edit-project-status" value={form.status_id || ""} onChange={(e) => setForm({ ...form, status_id: e.target.value })} className="w-full px-2 py-1.5 border border-neutral-300 rounded text-sm">
                                <option value="">—</option>
                                {statuses.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                            </select>
                        </F>
                        <F label="Scadenza"><input type="date" data-testid="edit-project-end-date" value={form.end_date || ""} onChange={(e) => setForm({ ...form, end_date: e.target.value })} className="w-full px-2 py-1.5 border border-neutral-300 rounded text-sm" /></F>
                    </div>
                </div>
                {err && <div className="mx-5 mb-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">{err}</div>}
                <div className="px-5 py-3 border-t border-neutral-100 flex justify-end gap-2 bg-neutral-50">
                    <button type="button" onClick={onClose} className="px-3 py-1.5 text-sm rounded border border-neutral-300 bg-white">Annulla</button>
                    <button type="submit" disabled={busy} data-testid="save-edit-project-btn" className="px-3 py-1.5 text-sm rounded bg-neutral-900 text-white hover:bg-neutral-800 disabled:opacity-60">{busy ? "Salvataggio…" : "Salva"}</button>
                </div>
            </form>
        </div>
    );
}

// ============= STRATEGY TAB =============
function StrategyTab({ project, programs, onChange }) {
    const editorRef = useRef(null);
    const [floatBtn, setFloatBtn] = useState(null);
    const [saveTimer, setSaveTimer] = useState(null);
    const mention = useMention(editorRef);
    const anchorById = Object.fromEntries(programs.filter((p) => p.plan_anchor_id).map((p) => [p.plan_anchor_id, p]));

    useEffect(() => {
        if (editorRef.current && editorRef.current.innerHTML !== (project.plan_text || "")) {
            editorRef.current.innerHTML = project.plan_text
                ? sanitizeHtml(project.plan_text)
                : "<p>Scrivi qui il piano strategico. Puoi selezionare una frase per creare un programma.</p>";
        }
        // eslint-disable-next-line
    }, [project.id]);

    const syncDetached = async () => {
        if (!editorRef.current) return;
        const html = editorRef.current.innerHTML;
        let mutated = false;
        for (const p of programs) {
            if (!p.plan_anchor_id) continue;
            const found = html.includes(`data-anchor-id="${p.plan_anchor_id}"`);
            if (found === !p.detached) continue;
            await api.patch(`/projects/programs/${p.id}`, { detached: !found });
            mutated = true;
        }
        if (mutated) onChange();
    };
    // eslint-disable-next-line
    useEffect(() => { syncDetached(); }, [programs.length]);

    useEffect(() => {
        if (!editorRef.current) return;
        editorRef.current.querySelectorAll(".plan-anchor").forEach((el) => {
            const aid = el.getAttribute("data-anchor-id");
            const prog = anchorById[aid];
            if (prog?.detached) el.classList.add("detached"); else el.classList.remove("detached");
        });
    });

    const onSelect = () => {
        const sel = window.getSelection();
        if (!sel || sel.rangeCount === 0 || sel.isCollapsed) { setFloatBtn(null); return; }
        const range = sel.getRangeAt(0);
        if (!editorRef.current.contains(range.commonAncestorContainer)) { setFloatBtn(null); return; }
        const text = sel.toString().trim();
        if (text.length < 3) { setFloatBtn(null); return; }
        const r = range.getBoundingClientRect();
        setFloatBtn({ x: r.left + r.width / 2 + window.scrollX, y: r.top + window.scrollY - 6, text, range });
    };

    const createProgram = async () => {
        if (!floatBtn) return;
        const anchorId = crypto.randomUUID();
        const span = document.createElement("span");
        span.className = "plan-anchor";
        span.setAttribute("data-anchor-id", anchorId);
        span.textContent = floatBtn.text;
        floatBtn.range.deleteContents();
        floatBtn.range.insertNode(span);
        window.getSelection().removeAllRanges();
        const newHtml = editorRef.current.innerHTML;
        await api.patch(`/projects/${project.id}`, { plan_text: newHtml });
        await api.post(`/projects/${project.id}/programs`, {
            project_id: project.id, title: floatBtn.text.slice(0, 80), description: "",
            plan_anchor_text: floatBtn.text, plan_anchor_id: anchorId, order: programs.length,
        });
        setFloatBtn(null);
        onChange();
    };

    const onInput = () => {
        if (saveTimer) clearTimeout(saveTimer);
        const t = setTimeout(async () => {
            const html = editorRef.current.innerHTML;
            await api.patch(`/projects/${project.id}`, { plan_text: html });
            await syncAnchorTexts();
            await syncDetached();
            await reorderByAnchors();
        }, 700);
        setSaveTimer(t);
    };

    // Keep each program.plan_anchor_text in sync with the current textContent
    // of its .plan-anchor span (so if the user edits the wording inside the
    // highlight, the program title-hint stays accurate).
    const syncAnchorTexts = async () => {
        if (!editorRef.current) return;
        for (const p of programs) {
            if (!p.plan_anchor_id) continue;
            const span = editorRef.current.querySelector(`.plan-anchor[data-anchor-id="${p.plan_anchor_id}"]`);
            if (!span) continue;
            const txt = (span.textContent || "").trim();
            if (txt && txt !== (p.plan_anchor_text || "").trim()) {
                await api.patch(`/projects/programs/${p.id}`, { plan_anchor_text: txt });
            }
        }
    };

    // Anchored programs follow the plan text order; free/detached ones stay
    // attached right after the anchored program that precedes them.
    const reorderByAnchors = async () => {
        if (!editorRef.current) return;
        const spans = Array.from(editorRef.current.querySelectorAll(".plan-anchor"));
        const anchorOrder = spans.map((s) => s.getAttribute("data-anchor-id")).filter(Boolean);
        const isPositioned = (p) => p.plan_anchor_id && !p.detached && anchorOrder.includes(p.plan_anchor_id);
        const sorted = programs.slice().sort((a, b) => (a.order || 0) - (b.order || 0));
        const head = [];
        const groups = [];
        for (const p of sorted) {
            if (isPositioned(p)) groups.push({ anchor: p, followers: [] });
            else if (groups.length) groups[groups.length - 1].followers.push(p);
            else head.push(p);
        }
        groups.sort((a, b) => anchorOrder.indexOf(a.anchor.plan_anchor_id) - anchorOrder.indexOf(b.anchor.plan_anchor_id));
        const desired = [...head, ...groups.flatMap((g) => [g.anchor, ...g.followers])];
        let mutated = false;
        for (let i = 0; i < desired.length; i++) {
            if ((desired[i].order || 0) !== i) {
                await api.patch(`/projects/programs/${desired[i].id}`, { order: i });
                mutated = true;
            }
        }
        if (mutated) onChange();
    };
    // Auto-reorder after programs load / plan-text is loaded
    useEffect(() => { reorderByAnchors(); /* eslint-disable-next-line */ }, [programs.length, project.plan_text]);

    const renameProgram = async (prog, newTitle) => {
        if (!newTitle.trim() || newTitle === prog.title) return;
        await api.patch(`/projects/programs/${prog.id}`, { title: newTitle.trim() });
        onChange();
    };

    const deleteProgram = async (prog) => {
        if (!window.confirm(`Eliminare il programma "${prog.title}"?\nVerranno eliminate anche tutte le task e task secondarie annidate. L'evidenziazione della frase nel piano verrà rimossa.`)) return;
        if (editorRef.current && prog.plan_anchor_id) {
            const span = editorRef.current.querySelector(`.plan-anchor[data-anchor-id="${prog.plan_anchor_id}"]`);
            if (span) {
                const parent = span.parentNode;
                while (span.firstChild) parent.insertBefore(span.firstChild, span);
                parent.removeChild(span);
                parent.normalize();
            }
            const newHtml = editorRef.current.innerHTML;
            await api.patch(`/projects/${project.id}`, { plan_text: newHtml });
        }
        await api.delete(`/projects/programs/${prog.id}`);
        onChange();
    };

    return (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-testid="strategy-tab">
            <div className="lg:col-span-2 bg-white border border-neutral-200 rounded-md p-6 relative">
                <div className="text-[11px] uppercase tracking-wider text-neutral-500 font-semibold mb-3">Piano strategico</div>
                <div ref={editorRef} contentEditable suppressContentEditableWarning
                    onMouseUp={(e) => { onSelect(); mention.handlers.onMouseUp(e); }}
                    onKeyUp={(e) => { onSelect(); mention.handlers.onKeyUp(e); }}
                    onInput={(e) => { onInput(); mention.handlers.onInput(e); }}
                    onClick={(e) => {
                        const el = e.target.closest?.(".policy-mention");
                        if (el) {
                            e.preventDefault();
                            const pid = el.getAttribute("data-policy-id");
                            if (pid) window.open(`/policy/${pid}`, "_blank", "noopener");
                        }
                    }}
                    className="plan-editor rich-content text-neutral-800" data-testid="plan-editor" />
                {floatBtn && (
                    <button onClick={createProgram} data-testid="create-program-btn" className="plan-float-btn" style={{ left: floatBtn.x, top: floatBtn.y }}>+ Crea programma</button>
                )}
                {mention.state && (
                    <MentionPopover
                        query={mention.state.query}
                        position={mention.state.position}
                        editorRef={editorRef}
                        onClose={mention.close}
                        onInserted={() => {
                            mention.close();
                            // Trigger the debounced auto-save
                            if (editorRef.current) {
                                api.patch(`/projects/${project.id}`, { plan_text: editorRef.current.innerHTML });
                            }
                        }}
                    />
                )}
            </div>
            <div className="bg-white border border-neutral-200 rounded-md">
                <div className="px-4 py-3 border-b border-neutral-100 font-display text-sm font-semibold">Programmi collegati</div>
                {programs.length === 0 ? (
                    <div className="p-6 text-sm text-neutral-400">Seleziona una frase nel piano e clicca <b>Crea programma</b>.</div>
                ) : (
                    <ul className="divide-y divide-neutral-100">
                        {programs.map((p) => (
                            <li key={p.id} className="px-4 py-2.5" data-testid={`strategy-program-${p.id}`}>
                                <InlineEditableTitle value={p.title} onChange={(v) => renameProgram(p, v)} testId={`rename-program-${p.id}`} />
                                <div className="text-xs text-neutral-500 truncate mt-0.5">{p.plan_anchor_text}</div>
                                <div className="flex items-center justify-between mt-1">
                                    {p.detached ? <span className="text-xs text-red-600">Scollegato dal piano</span> : <span />}
                                    <button onClick={() => deleteProgram(p)} data-testid={`delete-program-${p.id}`} className="text-xs text-neutral-400 hover:text-red-600 inline-flex items-center gap-1">
                                        <Trash2 size={12} /> Elimina
                                    </button>
                                </div>
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </div>
    );
}

function InlineEditableTitle({ value, onChange, testId, className = "" }) {
    const [editing, setEditing] = useState(false);
    const [val, setVal] = useState(value);
    useEffect(() => setVal(value), [value]);
    if (!editing) {
        return (
            <button onClick={(e) => { e.stopPropagation(); setEditing(true); }} data-testid={testId}
                className={`text-left font-medium text-neutral-900 hover:bg-neutral-50 rounded px-1 -mx-1 ${className}`}>
                {value}
            </button>
        );
    }
    const commit = () => { setEditing(false); if (val.trim() && val !== value) onChange(val.trim()); else setVal(value); };
    return (
        <input autoFocus value={val} onChange={(e) => setVal(e.target.value)}
            onBlur={commit}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); commit(); } if (e.key === "Escape") { setVal(value); setEditing(false); } }}
            data-testid={`${testId}-input`}
            className={`text-sm border border-neutral-300 rounded px-1 py-0.5 ${className}`} />
    );
}

// ============= PROGRAMS TAB =============
function ProgramsTab({ programs, tasks, metrics, showPct, onChange }) {
    const [applyOn, setApplyOn] = useState(null); // program object
    const [savingSop, setSavingSop] = useState(null); // program.id being saved
    const [creating, setCreating] = useState(false);
    const renameProgram = async (prog, newTitle) => {
        if (!newTitle.trim() || newTitle === prog.title) return;
        await api.patch(`/projects/programs/${prog.id}`, { title: newTitle.trim() });
        onChange();
    };
    const setDueDate = async (prog, due) => {
        await api.patch(`/projects/programs/${prog.id}`, { due_date: due || null });
        onChange();
    };
    const saveAsSop = async (prog) => {
        const name = window.prompt("Nome del template SOP:", `SOP · ${prog.title}`);
        if (!name) return;
        setSavingSop(prog.id);
        try {
            await api.post(`/sop-templates/from-program/${prog.id}`, { name });
            window.alert("Template SOP creato — lo trovi nella pagina SOP.");
        } catch (e) {
            window.alert(extractError(e));
        } finally {
            setSavingSop(null);
        }
    };
    const addFreeProgram = async () => {
        const title = window.prompt("Titolo del nuovo programma libero:");
        if (!title || !title.trim()) return;
        const projId = programs[0]?.project_id;
        if (!projId) { window.alert("Errore: project id non disponibile."); return; }
        setCreating(true);
        try {
            await api.post(`/projects/${projId}/programs`, {
                project_id: projId, title: title.trim(), description: "",
                order: programs.length,  // append at end (free programs live after anchored ones)
            });
            onChange();
        } catch (e) { window.alert(extractError(e)); }
        finally { setCreating(false); }
    };
    const reassignAnchor = async (prog) => {
        const phrase = window.prompt(
            `Riassocia "${prog.title}" al piano strategico.\n\nCopia qui una frase esatta del piano dove verrà creato il nuovo ancoraggio:`
        );
        if (!phrase || !phrase.trim()) return;
        try {
            await api.post(`/projects/programs/${prog.id}/reanchor`, { phrase: phrase.trim() });
            onChange();
        } catch (e) { window.alert(extractError(e)); }
    };
    // Drag & drop for FREE programs only (no plan_anchor_id or detached).
    // Anchored programs are auto-ordered by the plan text — draggable={false}.
    // Free programs can be dropped anywhere, anchored ones keep their relative order.
    const handleDrop = async (srcId, dstId) => {
        if (srcId === dstId) return;
        const reord = programs.slice().sort((a, b) => (a.order || 0) - (b.order || 0));
        const srcIdx = reord.findIndex((p) => p.id === srcId);
        const dstIdx = reord.findIndex((p) => p.id === dstId);
        if (srcIdx < 0 || dstIdx < 0) return;
        const [moved] = reord.splice(srcIdx, 1);
        reord.splice(dstIdx, 0, moved);
        for (let i = 0; i < reord.length; i++) {
            if ((reord[i].order || 0) !== i) {
                await api.patch(`/projects/programs/${reord[i].id}`, { order: i });
            }
        }
        onChange();
    };

    return (
        <div className="space-y-3" data-testid="programs-tab">
            {programs.length === 0 && <EmptyBox text="Nessun programma. Vai in Strategia e seleziona una frase del piano, oppure crea un programma libero." />}
            {programs.map((p) => {
                const m = metrics.programs?.[p.id] || {};
                const secs = tasks.filter((t) => t.level === "secondary" && t.program_id === p.id);
                const est = secs.reduce((s, t) => s + (Number(t.estimated_hours) || 0), 0);
                const act = secs.reduce((s, t) => s + (Number(t.actual_hours) || 0), 0);
                const ratio = productivityRatio(est, act);
                const isFree = !p.plan_anchor_id || p.detached;
                return (
                    <div key={p.id} className={`bg-white border rounded-md p-4 ${p.detached ? "border-red-200" : "border-neutral-200"}`}
                        data-testid={`program-${p.id}`}
                        draggable={isFree}
                        onDragStart={isFree ? (e) => { e.dataTransfer.setData("free-prog-id", p.id); e.dataTransfer.effectAllowed = "move"; } : undefined}
                        onDragOver={(e) => { if (e.dataTransfer.types.includes("free-prog-id")) e.preventDefault(); }}
                        onDrop={(e) => { const src = e.dataTransfer.getData("free-prog-id"); if (src) handleDrop(src, p.id); }}>
                        <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-2">
                                    {isFree && <span className="text-neutral-300 cursor-grab" title="Trascina per riordinare (solo programmi liberi)">⋮⋮</span>}
                                    <InlineEditableTitle value={p.title} onChange={(v) => renameProgram(p, v)} testId={`programs-rename-${p.id}`} className="font-display font-semibold text-neutral-900 text-base" />
                                    {p.detached && (
                                        <span className="text-[10px] uppercase tracking-wider text-red-700 bg-red-50 border border-red-200 rounded px-1.5 py-0.5">
                                            Separato dal piano
                                        </span>
                                    )}
                                    {!p.plan_anchor_id && !p.detached && (
                                        <span className="text-[10px] uppercase tracking-wider text-neutral-500 bg-neutral-100 border border-neutral-200 rounded px-1.5 py-0.5">
                                            Libero
                                        </span>
                                    )}
                                </div>
                                {p.plan_anchor_text && !p.detached && <div className="text-xs text-neutral-500 mt-0.5 italic">"{p.plan_anchor_text}"</div>}
                                {p.description && <div className="text-sm text-neutral-700 mt-2">{p.description}</div>}
                            </div>
                            {showPct && (
                                <div className="text-right">
                                    <div className="text-2xl font-display font-semibold text-neutral-900">{m.percentage ?? 0}%</div>
                                    <div className="text-[11px] text-neutral-500">{m.done ?? 0}/{m.total ?? 0} task</div>
                                </div>
                            )}
                        </div>
                        <div className="mt-3 flex flex-wrap gap-3 text-xs text-neutral-600 items-center">
                            <label className="inline-flex items-center gap-1">Scadenza:
                                <input type="date" value={p.due_date || ""} onChange={(e) => setDueDate(p, e.target.value)}
                                    data-testid={`program-due-date-${p.id}`} className="text-xs border border-neutral-200 rounded px-1.5 py-0.5" />
                            </label>
                            <span className="inline-flex items-center gap-1" data-testid={`program-est-${p.id}`}>
                                Ore stimate: <b>{formatHM(est) || "—"}</b>
                                <span title="Somma dalle task" className="text-[10px] px-1 py-0.5 border border-neutral-200 rounded text-neutral-500">Σ</span>
                            </span>
                            <span className="inline-flex items-center gap-1" data-testid={`program-act-${p.id}`}>
                                Ore effettive: <b>{formatHM(act) || "—"}</b>
                                <span title="Somma dalle task" className="text-[10px] px-1 py-0.5 border border-neutral-200 rounded text-neutral-500">Σ</span>
                            </span>
                            {ratio != null && <span className={productivityClass(ratio)}>Produttività: {ratio.toFixed(2)}x</span>}
                            <button onClick={() => setApplyOn(p)} data-testid={`apply-sop-${p.id}`}
                                className="ml-auto text-xs border border-neutral-200 rounded px-2 py-1 hover:bg-neutral-50 inline-flex items-center gap-1 text-neutral-700">
                                <BookMarked size={12} /> Applica template
                            </button>
                            <button onClick={() => saveAsSop(p)} disabled={savingSop === p.id} data-testid={`save-as-sop-${p.id}`}
                                className="text-xs border border-neutral-200 rounded px-2 py-1 hover:bg-neutral-50 inline-flex items-center gap-1 text-neutral-700 disabled:opacity-60">
                                <BookMarked size={12} /> {savingSop === p.id ? "Salvo…" : "Salva come SOP"}
                            </button>
                            {p.detached && (
                                <button onClick={() => reassignAnchor(p)} data-testid={`reanchor-${p.id}`}
                                    className="text-xs border border-red-200 bg-red-50 rounded px-2 py-1 hover:bg-red-100 inline-flex items-center gap-1 text-red-700">
                                    Riassocia
                                </button>
                            )}
                        </div>
                    </div>
                );
            })}
            <div className="flex justify-end">
                <button onClick={addFreeProgram} disabled={creating} data-testid="add-free-program"
                    className="text-xs border border-neutral-300 rounded px-3 py-1.5 hover:bg-neutral-50 inline-flex items-center gap-1 text-neutral-700 disabled:opacity-60">
                    <Plus size={12} /> {creating ? "Creazione…" : "Aggiungi programma libero"}
                </button>
            </div>
            {applyOn && (
                <ApplyProgramTemplateModal program={applyOn}
                    onClose={() => setApplyOn(null)}
                    onApplied={() => { setApplyOn(null); onChange(); }} />
            )}
        </div>
    );
}

function ApplyProgramTemplateModal({ program, onClose, onApplied }) {
    const [templates, setTemplates] = useState([]);
    const [tid, setTid] = useState("");
    const [replace, setReplace] = useState(false);
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");
    useEffect(() => {
        api.get("/sop-templates", { params: { type: "program" } }).then((r) => setTemplates(r.data)).catch(() => {});
    }, []);
    const apply = async () => {
        if (!tid) return;
        if (replace && !window.confirm("Sostituire integralmente le task del programma con quelle del template? Le task esistenti verranno cancellate.")) return;
        setBusy(true); setErr("");
        try { await api.post(`/sop-templates/${tid}/apply/program/${program.id}`, null, { params: { replace } }); onApplied(); }
        catch (e) { setErr(extractError(e)); } finally { setBusy(false); }
    };
    return (
        <div className="fixed inset-0 bg-neutral-900/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
            <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-md shadow-lg w-full max-w-md" data-testid="apply-program-template-modal">
                <div className="px-5 py-3 border-b border-neutral-200 flex items-center justify-between">
                    <div className="font-display font-semibold">Applica template al programma</div>
                    <button onClick={onClose}><X size={16} /></button>
                </div>
                <div className="p-5 space-y-3">
                    <div className="text-sm text-neutral-600">Programma: <b className="text-neutral-900">{program.title}</b></div>
                    {templates.length === 0 ? (
                        <div className="text-sm text-neutral-500 bg-neutral-50 border border-neutral-200 rounded px-3 py-2">
                            Nessun template di programma disponibile. Creane uno in <Link to="/sop" className="underline">SOP</Link>.
                        </div>
                    ) : (
                        <label className="block">
                            <span className="text-xs text-neutral-600">Template</span>
                            <select value={tid} onChange={(e) => setTid(e.target.value)}
                                data-testid="select-program-template"
                                className="mt-1 w-full text-sm border border-neutral-300 rounded px-2 py-1.5">
                                <option value="">— Seleziona —</option>
                                {templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                            </select>
                        </label>
                    )}
                    <label className="flex items-start gap-2 text-sm">
                        <input type="checkbox" checked={replace} onChange={(e) => setReplace(e.target.checked)} data-testid="apply-replace-toggle" className="mt-0.5" />
                        <span>Sostituisci integralmente le task esistenti del programma</span>
                    </label>
                    {err && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">{err}</div>}
                </div>
                <div className="px-5 py-3 border-t border-neutral-100 flex justify-end gap-2 bg-neutral-50">
                    <button onClick={onClose} className="px-3 py-1.5 text-sm rounded border border-neutral-300 bg-white">Annulla</button>
                    <button onClick={apply} disabled={!tid || busy} data-testid="confirm-apply-program-template"
                        className="px-3 py-1.5 text-sm rounded bg-neutral-900 text-white hover:bg-neutral-800 disabled:opacity-60">
                        {busy ? "Applicazione…" : "Applica"}
                    </button>
                </div>
            </div>
        </div>
    );
}

function FeedbackModal({ projectId, onClose }) {
    const [state, setState] = useState({ loading: true, preview: null, err: "", busy: false, sent: null });
    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get(`/feedback/projects/${projectId}/preview`);
                setState((s) => ({ ...s, loading: false, preview: data }));
            } catch (e) {
                setState((s) => ({ ...s, loading: false, err: extractError(e) }));
            }
        })();
    }, [projectId]);

    const send = async () => {
        setState((s) => ({ ...s, busy: true, err: "" }));
        try {
            const { data } = await api.post(`/feedback/projects/${projectId}/send`);
            setState((s) => ({ ...s, busy: false, sent: data }));
        } catch (e) {
            setState((s) => ({ ...s, busy: false, err: extractError(e) }));
        }
    };

    const p = state.preview;
    const canSend = p && p.recipient && p.form_url && !state.sent;

    return (
        <div className="fixed inset-0 bg-neutral-900/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
            <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-md shadow-lg w-full max-w-lg" data-testid="feedback-modal">
                <div className="px-5 py-3 border-b border-neutral-200 flex items-center justify-between">
                    <div>
                        <div className="text-xs text-neutral-500">Progetto concluso</div>
                        <div className="font-display font-semibold">Invia email di feedback</div>
                    </div>
                    <button onClick={onClose}><X size={16} /></button>
                </div>
                <div className="p-5 space-y-3 text-sm">
                    {state.loading && <div className="text-neutral-500">Caricamento…</div>}
                    {!state.loading && p && (
                        <>
                            <div className="grid grid-cols-3 gap-2 text-xs">
                                <div className="text-neutral-500">Destinatario</div>
                                <div className="col-span-2 text-neutral-900 break-all" data-testid="feedback-recipient">{p.recipient || <span className="text-red-600">Il cliente non ha email</span>}</div>
                                <div className="text-neutral-500">Progetto</div>
                                <div className="col-span-2 text-neutral-900">{p.project_name}</div>
                                <div className="text-neutral-500">Cliente</div>
                                <div className="col-span-2 text-neutral-900">{p.client_name}</div>
                                <div className="text-neutral-500">Link questionario</div>
                                <div className="col-span-2 text-neutral-900 break-all">{p.form_url || <span className="text-red-600">Non configurato in Impostazioni → Preferenze</span>}</div>
                            </div>
                            <div>
                                <div className="text-xs text-neutral-500 mb-1">Oggetto</div>
                                <div className="text-sm text-neutral-800 bg-neutral-50 border border-neutral-200 rounded px-2 py-1.5">{p.subject}</div>
                            </div>
                            <div>
                                <div className="text-xs text-neutral-500 mb-1">Corpo (anteprima)</div>
                                <div className="text-sm text-neutral-800 bg-neutral-50 border border-neutral-200 rounded px-2 py-1.5 whitespace-pre-line">{p.body_plain}</div>
                            </div>
                        </>
                    )}
                    {state.err && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2" data-testid="feedback-error">{state.err}</div>}
                    {state.sent && <div className="text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-3 py-2" data-testid="feedback-sent">✓ Email inviata con successo</div>}
                </div>
                <div className="px-5 py-3 border-t border-neutral-100 flex justify-end gap-2 bg-neutral-50">
                    <button onClick={onClose} className="px-3 py-1.5 text-sm rounded border border-neutral-300 bg-white" data-testid="feedback-cancel">
                        {state.sent ? "Chiudi" : "Non inviare"}
                    </button>
                    {!state.sent && (
                        <button onClick={send} disabled={!canSend || state.busy} data-testid="feedback-send"
                            className="px-3 py-1.5 text-sm rounded bg-neutral-900 text-white hover:bg-neutral-800 disabled:opacity-60 inline-flex items-center gap-1">
                            <Send size={13} /> {state.busy ? "Invio…" : "Invia email"}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}

// ============= TASKS TAB =============
function TasksTab({ level, project, programs, tasks, metrics, showPct, onChange }) {
    const secondaries = tasks.filter((t) => t.level === "secondary").sort((a, b) => (a.order || 0) - (b.order || 0));
    const tertiaries = tasks.filter((t) => t.level === "tertiary").sort((a, b) => (a.order || 0) - (b.order || 0));
    const childCount = buildChildCount(tertiaries);

    if (level === "secondary") {
        return (
            <div className="space-y-4" data-testid="secondary-tab">
                {programs.length === 0 && <EmptyBox text="Crea prima un programma dalla tab Strategia." />}
                {programs.map((p) => {
                    const items = secondaries.filter((t) => t.program_id === p.id);
                    return (
                        <TaskGroup key={p.id} groupKey={p.id} groupField="program_id" title={p.title}
                            allTasks={secondaries} items={items} project={project}
                            level="secondary" childCount={childCount} metrics={metrics} showPct={showPct}
                            onChange={onChange} />
                    );
                })}
            </div>
        );
    }

    // TERTIARY tab: for each program, for each secondary — either its tertiaries OR the secondary itself if it has no children.
    return (
        <div className="space-y-4" data-testid="tertiary-tab">
            {programs.length === 0 && <EmptyBox text="Nessun programma disponibile." />}
            {programs.map((p) => {
                const secs = secondaries.filter((s) => s.program_id === p.id);
                if (secs.length === 0) return null;
                return (
                    <div key={p.id} className="bg-white border border-neutral-200 rounded-md">
                        <div className="px-4 py-2 border-b border-neutral-100 font-display text-sm font-semibold">{p.title}</div>
                        <div className="p-3 space-y-3">
                            {secs.map((s) => {
                                const kids = tertiaries.filter((t) => t.parent_task_id === s.id);
                                if (kids.length === 0) {
                                    // Empty container, flaggable — visually different from a task secondaria row
                                    return (
                                        <div key={s.id} className="border border-neutral-200 rounded-md overflow-hidden bg-white" data-testid={`leaf-secondary-${s.id}`}>
                                            <div className="bg-neutral-50 border-b border-neutral-100">
                                                <TaskRow task={s} onChange={onChange} metrics={metrics} showPct={false} childCount={0} onDropAt={() => {}} />
                                            </div>
                                            <div className="px-3 py-4 flex items-center justify-between gap-2">
                                                <div className="text-xs text-neutral-400 italic">Contenitore vuoto — nessuna task secondaria annidata</div>
                                                <button onClick={() => addTertiaryWithWarn({ project, parent: s, existingCount: 0, onChange })}
                                                    data-testid={`add-nested-${s.id}`}
                                                    className="text-xs border border-neutral-300 rounded px-2 py-1 hover:bg-neutral-50 inline-flex items-center gap-1">
                                                    <Plus size={12} /> Aggiungi task secondaria
                                                </button>
                                            </div>
                                        </div>
                                    );
                                }
                                return (
                                    <TaskGroup key={s.id} nested groupKey={s.id} groupField="parent_task_id" title={s.title}
                                        allTasks={tertiaries} items={kids} project={project}
                                        level="tertiary" metrics={metrics} showPct={false}
                                        parent={s} onChange={onChange} />
                                );
                            })}
                        </div>
                    </div>
                );
            })}
        </div>
    );
}

function TaskGroupAddBtn({ onAdd, groupKey }) {
    return (
        <button onClick={onAdd} className="text-xs text-neutral-500 hover:text-neutral-900 inline-flex items-center gap-1" data-testid={`add-task-${groupKey}`}>
            <Plus size={12} /> Aggiungi
        </button>
    );
}

async function addTertiaryWithWarn({ project, parent, existingCount = 0, onChange }) {
    // Warn if this is first tertiary and parent has manual data (hours or completed)
    if (existingCount === 0) {
        const hasManual = (Number(parent.estimated_hours) || 0) > 0 || (Number(parent.actual_hours) || 0) > 0 || parent.completed;
        if (hasManual) {
            const ok = window.confirm(
                'Attenzione: aggiungendo una task secondaria le ore stimate/effettive di questa task saranno sostituite dalla somma delle task secondarie, e lo stato di completamento sarà calcolato automaticamente. Continuare?'
            );
            if (!ok) return;
        }
    }
    const title = window.prompt("Titolo:");
    if (!title) return;
    try {
        await api.post(`/projects/${project.id}/tasks`, {
            project_id: project.id, level: "tertiary", parent_task_id: parent.id,
            title, task_type: "semplice", completed: false, order: existingCount,
        });
    } catch (e) { toast.error(extractError(e)); return; }
    onChange();
}

function TaskGroup({ nested, groupKey, groupField, title, items, allTasks, project, level, parent, childCount, metrics, showPct, onChange }) {
    const [dragOver, setDragOver] = useState(false);
    const addTask = async () => {
        if (level === "tertiary" && parent) {
            await addTertiaryWithWarn({ project, parent, existingCount: items.length, onChange });
            return;
        }
        const t = window.prompt("Titolo:");
        if (!t) return;
        const body = { project_id: project.id, level, title: t, task_type: "semplice", completed: false, order: items.length };
        body[groupField] = groupKey;
        try { await api.post(`/projects/${project.id}/tasks`, body); } catch (e) { toast.error(extractError(e)); return; }
        onChange();
    };

    const handleDrop = async (e, insertAtIndex = null) => {
        e.preventDefault();
        setDragOver(false);
        const draggedId = e.dataTransfer.getData("text/plain");
        if (!draggedId) return;
        const dragged = allTasks.find((t) => t.id === draggedId);
        if (!dragged) return;
        const groupItems = items.filter((t) => t.id !== draggedId);
        const targetIdx = insertAtIndex == null ? groupItems.length : Math.max(0, Math.min(insertAtIndex, groupItems.length));
        groupItems.splice(targetIdx, 0, dragged);
        for (let i = 0; i < groupItems.length; i++) {
            const t = groupItems[i];
            const update = {};
            if ((t.order || 0) !== i) update.order = i;
            if (t.id === dragged.id && t[groupField] !== groupKey) update[groupField] = groupKey;
            if (Object.keys(update).length > 0) {
                try { await api.patch(`/projects/tasks/${t.id}`, update); } catch (err) { toast.error(extractError(err)); break; }
            }
        }
        onChange();
    };

    return (
        <div className={nested ? "border border-neutral-100 rounded" : "bg-white border border-neutral-200 rounded-md"} data-testid={`group-${groupField}-${groupKey}`}>
            <div className={`flex items-center justify-between px-3 py-2 ${nested ? "bg-neutral-50" : "border-b border-neutral-100"} ${dragOver ? "bg-emerald-50" : ""}`}
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={(e) => handleDrop(e, null)}>
                <div className="font-display text-sm font-semibold text-neutral-800">{title}</div>
                <TaskGroupAddBtn onAdd={addTask} groupKey={groupKey} />
            </div>
            <div className="p-2 space-y-1" onDragOver={(e) => e.preventDefault()} onDrop={(e) => handleDrop(e, null)}>
                {items.length === 0 && <EmptyRow text="Nessuna task." />}
                {items.map((t, idx) => (
                    <TaskRow key={t.id} task={t} onChange={onChange} metrics={metrics} showPct={showPct}
                        childCount={childCount?.[t.id] || 0}
                        onDropAt={(e) => handleDrop(e, idx)} />
                ))}
            </div>
        </div>
    );
}

// ============= TASK ROW =============
function TaskRow({ task, onChange, metrics, showPct, childCount, onDropAt, showAsLeaf }) {
    const meta = taskTypeMeta(task.task_type);
    const secMetric = metrics.secondary_tasks?.[task.id];
    const ratio = productivityRatio(task.estimated_hours, task.actual_hours);
    const [dragOver, setDragOver] = useState(false);
    const isDerived = task.level === "secondary" && (childCount || 0) > 0;

    // Local hours inputs (H:MM strings) to allow typing "1:45"
    const [estStr, setEstStr] = useState(formatHM(task.estimated_hours));
    const [actStr, setActStr] = useState(formatHM(task.actual_hours));
    useEffect(() => { setEstStr(formatHM(task.estimated_hours)); }, [task.estimated_hours]);
    useEffect(() => { setActStr(formatHM(task.actual_hours)); }, [task.actual_hours]);

    const toggle = async () => {
        if (isDerived) return; // read-only when derived
        if (task.task_type === "approvazione") {
            const approved = !task.approved;
            await api.patch(`/projects/tasks/${task.id}`, { approved, completed: approved });
        } else {
            await api.patch(`/projects/tasks/${task.id}`, { completed: !task.completed });
        }
        onChange();
    };

    const cycleCondition = async () => {
        const seq = ["da_verificare", "verificata", "non_verificata"];
        const cur = task.condition_state || "da_verificare";
        const next = seq[(seq.indexOf(cur) + 1) % seq.length];
        await api.patch(`/projects/tasks/${task.id}`, { condition_state: next, completed: next === "verificata" });
        onChange();
    };

    const changeType = async (e) => { await api.patch(`/projects/tasks/${task.id}`, { task_type: e.target.value }); onChange(); };
    const del = async () => { if (!window.confirm("Eliminare la task?")) return; await api.delete(`/projects/tasks/${task.id}`); onChange(); };
    const duplicate = async () => { await api.post(`/projects/tasks/${task.id}/duplicate`); onChange(); };
    const commitHours = async (field, str, setLocal) => {
        if (isDerived) return;
        const parsed = parseHM(str);
        const val = parsed == null ? 0 : parsed;
        setLocal(formatHM(val));
        if ((Number(task[field]) || 0) !== val) {
            await api.patch(`/projects/tasks/${task.id}`, { [field]: val });
            onChange();
        }
    };
    const rename = async (newTitle) => {
        if (!newTitle.trim() || newTitle === task.title) return;
        await api.patch(`/projects/tasks/${task.id}`, { title: newTitle.trim() });
        onChange();
    };

    return (
        <div className={`flex items-center gap-2 px-2 py-1.5 rounded border transition-colors ${dragOver ? "border-[#FFE500] bg-emerald-50/40" : "border-transparent hover:border-neutral-100 hover:bg-neutral-50"}`}
            data-testid={`task-${task.id}`}
            draggable
            onDragStart={(e) => { e.dataTransfer.setData("text/plain", task.id); e.dataTransfer.effectAllowed = "move"; }}
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(e) => { setDragOver(false); e.stopPropagation(); onDropAt(e); }}>
            <span className="text-neutral-300 cursor-grab select-none" title="Trascina per riordinare">⋮⋮</span>
            <button onClick={toggle} disabled={isDerived}
                className={`${isDerived ? "text-neutral-300 cursor-not-allowed" : "text-neutral-500 hover:text-neutral-900"}`}
                title={isDerived ? "Completamento calcolato dalle task secondarie" : ""}
                data-testid={`toggle-${task.id}`}>
                {task.completed ? <CheckCircle2 size={16} className={isDerived ? "text-emerald-400" : "text-emerald-600"} /> : <Circle size={16} />}
            </button>
            {!isDerived && (
                <select value={task.task_type} onChange={changeType} className={`text-xs px-1.5 py-0.5 rounded border ${meta.cls}`} data-testid={`task-type-${task.id}`}>
                    {TASK_TYPES.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
                </select>
            )}
            <div className={`flex-1 min-w-0 ${task.completed ? "line-through text-neutral-400" : ""}`}>
                <InlineEditableTitle value={task.title} onChange={rename} testId={`rename-task-${task.id}`} className="text-sm block truncate max-w-full" />
            </div>
            {isDerived && (
                <span className="inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-700 border border-indigo-100" data-testid={`derived-badge-${task.id}`}>
                    <Lock size={10} /> Auto (Σ da {childCount})
                </span>
            )}
            {!isDerived && childCount > 0 && (
                <span className="inline-flex items-center gap-0.5 text-xs px-1.5 py-0.5 rounded border bg-neutral-50 text-neutral-600 border-neutral-200" title="Contiene task secondarie" data-testid={`children-count-${task.id}`}>
                    <ListTree size={11} /> {childCount}
                </span>
            )}
            {task.completed && (
                <input
                    type="date"
                    value={task.completed_at ? String(task.completed_at).slice(0, 10) : ""}
                    onChange={async (e) => {
                        const d = e.target.value;
                        if (!d) return;
                        // Preserve time-of-day if present; otherwise noon UTC
                        const iso = new Date(`${d}T12:00:00Z`).toISOString();
                        await api.patch(`/projects/tasks/${task.id}`, { completed_at: iso });
                        onChange();
                    }}
                    data-testid={`completed-at-${task.id}`}
                    title="Retrodata esecuzione"
                    className="text-[11px] text-emerald-800 bg-emerald-50 border border-emerald-200 rounded px-1.5 py-0.5 focus:outline-none focus:border-emerald-400"
                />
            )}
            {task.task_type === "condizionale" && (
                <button onClick={cycleCondition} className="text-xs px-1.5 py-0.5 rounded border bg-amber-50 border-amber-200 text-amber-800">
                    {task.condition_state === "verificata" ? "Verificata" : task.condition_state === "non_verificata" ? "Non verificata" : "Da verificare"}
                </button>
            )}
            {showPct && secMetric != null && <span className="text-xs text-neutral-500">{secMetric.percentage}%</span>}
            <input type="text" placeholder="0:00" value={estStr}
                onChange={(e) => setEstStr(e.target.value)}
                onBlur={(e) => commitHours("estimated_hours", e.target.value, setEstStr)}
                onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
                disabled={isDerived}
                data-testid={`est-hours-${task.id}`}
                className={`w-16 text-xs border border-neutral-200 rounded px-1 py-0.5 text-right ${isDerived ? "bg-neutral-100 text-neutral-500 cursor-not-allowed" : ""}`}
                title="Ore stimate (H:MM)" />
            <input type="text" placeholder="0:00" value={actStr}
                onChange={(e) => setActStr(e.target.value)}
                onBlur={(e) => commitHours("actual_hours", e.target.value, setActStr)}
                onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
                disabled={isDerived}
                data-testid={`act-hours-${task.id}`}
                className={`w-16 text-xs border border-neutral-200 rounded px-1 py-0.5 text-right ${isDerived ? "bg-neutral-100 text-neutral-500 cursor-not-allowed" : ""}`}
                title="Ore effettive (H:MM)" />
            {ratio != null && <span className={`text-xs ${productivityClass(ratio)}`}>{ratio.toFixed(2)}x</span>}
            {!task.completed && !((childCount || 0) > 0) && <AddToPlanBtn task={task} />}
            {!showAsLeaf && (
                <>
                    <button onClick={duplicate} className="text-neutral-400 hover:text-neutral-800" data-testid={`duplicate-task-${task.id}`} title="Duplica task"><Copy size={13} /></button>
                    <button onClick={del} className="text-neutral-400 hover:text-red-600" data-testid={`delete-task-${task.id}`}><Trash2 size={13} /></button>
                </>
            )}
        </div>
    );
}

function EmptyBox({ text }) { return <div className="bg-white border border-dashed border-neutral-200 rounded-md p-8 text-center text-sm text-neutral-400">{text}</div>; }
function EmptyRow({ text }) { return <div className="text-xs text-neutral-400 px-2 py-1">{text}</div>; }
function F({ label, children }) { return <label className="block"><span className="text-xs font-medium text-neutral-600">{label}</span><div className="mt-1">{children}</div></label>; }
